import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardHeader, CardBody, Button, Badge } from "reactstrap";
import { fetchAPI } from "../utils/api";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const SEVERITY_OPTIONS = [
  { label: 'All',      value: 'all'      },
  { label: 'Critical', value: 'critical' },
  { label: 'High',     value: 'high'     },
  { label: 'Medium',   value: 'medium'   },
  { label: 'Low',      value: 'low'      },
];

const SEVERITY_TO_LEVEL = { all: null, critical: 12, high: 7, medium: 4, low: 0 };

const COLS = ['Time', 'Asset', 'Threat Type', 'Description', 'Severity', 'Action'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getSeverityKey = (criticality, ruleLevel) => {
  if (criticality) return criticality.toLowerCase();
  if (ruleLevel != null) {
    const lvl = parseInt(ruleLevel, 10);
    if (lvl >= 12) return 'critical';
    if (lvl >= 7)  return 'high';
    if (lvl >= 4)  return 'medium';
  }
  return 'low';
};

// ─── Original badge helpers (unchanged) ───────────────────────────────────────
const getSeverityBadge = (criticality, ruleLevel) => {
  let severity = 'Low';
  let color = '#198754';
  let bgColor = 'rgba(25, 135, 84, 0.1)';
  let borderColor = 'rgba(25, 135, 84, 0.3)';

  if (criticality) {
    severity = criticality;
  } else if (ruleLevel) {
    const level = parseInt(ruleLevel);
    if (level >= 12) severity = 'Critical';
    else if (level >= 7) severity = 'High';
    else if (level >= 4) severity = 'Medium';
  }

  switch (severity.toLowerCase()) {
    case 'critical':
      color = '#8b0000';
      bgColor = 'rgba(139, 0, 0, 0.1)';
      borderColor = 'rgba(139, 0, 0, 0.3)';
      break;
    case 'high':
      color = '#dc3545';
      bgColor = 'rgba(220, 53, 69, 0.1)';
      borderColor = 'rgba(220, 53, 69, 0.3)';
      break;
    case 'medium':
      color = '#ffc107';
      bgColor = 'rgba(255, 193, 7, 0.1)';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      break;
  }

  return (
    <Badge
      className="bg-transparent"
      style={{
        backgroundColor: bgColor,
        color: color,
        fontSize: '11px',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: '4px',
        border: `1px solid ${borderColor}`,
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
      }}
    >
      {severity}
    </Badge>
  );
};

const getThreatTypeBadge = (type) => (
  <Badge
    className="bg-transparent"
    style={{
      backgroundColor: `${ORANGE_COLORS.primary}15`,
      color: ORANGE_COLORS.primary,
      fontSize: '11px',
      fontWeight: 600,
      padding: '4px 10px',
      borderRadius: '4px',
      border: `1px solid ${ORANGE_COLORS.primary}30`
    }}
  >
    {type || 'Unknown'}
  </Badge>
);

// ─── Skeleton row — shows table structure while loading instead of a spinner ──
const SkeletonRow = () => (
  <tr>
    {[140, 120, 100, 240, 80, 60].map((w, i) => (
      <td key={i} style={{ padding: '12px 16px' }}>
        <div style={{
          height: '12px',
          width: `${w}px`,
          maxWidth: '100%',
          borderRadius: '6px',
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'wtable-shimmer 1.4s infinite',
        }} />
      </td>
    ))}
  </tr>
);

// ─── Main component ───────────────────────────────────────────────────────────
const WindowsThreatsTable = ({ loading }) => {
  const [threats, setThreats]           = useState([]);
  const [currentPage, setCurrentPage]   = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [tableLoading, setTableLoading] = useState(true);
  const [severity, setSeverity]         = useState('all');
  const abortRef                        = useRef(null);

  const fetchThreats = useCallback(async (page, sev) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setTableLoading(true);
    try {
      let url = `/threats?page=${page}&per_page=10&agent_type=windows`;
      const minLevel = SEVERITY_TO_LEVEL[sev];
      if (minLevel !== null) url += `&min_level=${minLevel}`;

      const response = await fetchAPI(url, { signal: abortRef.current.signal });
      if (response?.success) {
        let rows = response.threats || [];
        // Client-side fallback if backend ignores min_level param
        if (sev !== 'all') {
          rows = rows.filter(t => getSeverityKey(t.asset_criticality, t.wazuh_rule_level) === sev);
        }
        setThreats(rows);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching Windows threats:', err);
    } finally {
      setTableLoading(false);
    }
  }, []);

  // Reset to page 1 when severity filter changes
  useEffect(() => { setCurrentPage(1); }, [severity]);

  useEffect(() => {
    if (!loading) fetchThreats(currentPage, severity);
  }, [currentPage, severity, loading, fetchThreats]);

  const isLoading = loading || tableLoading;

  return (
    <>
      <style>{`
        @keyframes wtable-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <Card style={{
        ...threatCard
      }}>
        <CardHeader style={threatCardHeader}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

            {/* Title — original markup */}
            <div className="d-flex align-items-center">
              <div className="me-3" style={threatIconChip()}>
                <i className="ri-windows-line" style={{ color: "var(--vz-primary)", fontSize: 16 }} />
              </div>
              <div>
                <h5 className="card-title mb-0" style={{ fontSize: '16px' }}>Windows Alerts</h5>
                <small className="text-muted">
                  {isLoading ? 'Loading Windows threat data...' : `Showing ${threats.length} Windows threats`}
                </small>
              </div>
            </div>

            {/* Severity filter pills + original pagination */}
            <div className="d-flex align-items-center flex-wrap gap-2">

              {/* NEW: severity filter pills */}
              <div className="d-flex gap-1" style={{ flexWrap: 'wrap' }}>
                {SEVERITY_OPTIONS.map(opt => {
                  const isActive = severity === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setSeverity(opt.value)}
                      style={{
                        padding: '3px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        borderRadius: '20px',
                        border: `1px solid ${isActive ? ORANGE_COLORS.primary : 'rgba(0,0,0,0.15)'}`,
                        backgroundColor: isActive ? `${ORANGE_COLORS.primary}15` : 'transparent',
                        color: isActive ? ORANGE_COLORS.primary : '#6c757d',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        lineHeight: '1.6',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Original pagination buttons */}
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    backgroundColor: currentPage === 1 ? '#f0f0f0' : 'transparent',
                    borderColor: currentPage === 1 ? '#ccc' : ORANGE_COLORS.primary,
                    color: currentPage === 1 ? '#999' : ORANGE_COLORS.primary
                  }}
                >
                  <i className="ri-arrow-left-line" />
                </Button>
                <span className="px-3 py-1 d-flex align-items-center" style={{ fontSize: '13px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    backgroundColor: currentPage === totalPages ? '#f0f0f0' : 'transparent',
                    borderColor: currentPage === totalPages ? '#ccc' : ORANGE_COLORS.primary,
                    color: currentPage === totalPages ? '#999' : ORANGE_COLORS.primary
                  }}
                >
                  <i className="ri-arrow-right-line" />
                </Button>
              </div>
            </div>

          </div>
        </CardHeader>

        <CardBody className="p-0" style={threatCardBody}>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {COLS.map(col => (
                    <th key={col} style={{
                      padding: '12px 16px', fontSize: '11px',
                      fontWeight: '600', textTransform: 'uppercase',
                      textAlign: col === 'Action' ? 'center' : 'left'
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                ) : threats.length > 0 ? (
                  threats.map((threat, index) => (
                    <tr key={threat.threat_id ?? index}>
                      <td style={{ padding: '12px 16px', fontSize: '11px', fontFamily: 'monospace', color: '#6c757d' }}>
                        {new Date(threat.timestamp).toLocaleString('en-US', {
                          month: 'short', day: '2-digit',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '500' }}>
                          {threat.agent_name || threat.asset_name}
                        </div>
                        {threat.wazuh_rule_id && (
                          <div style={{ fontSize: '10px', color: '#6c757d', fontFamily: 'monospace' }}>
                            Rule: {threat.wazuh_rule_id}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {getThreatTypeBadge(threat.threat_type)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{
                          maxWidth: '300px', whiteSpace: 'normal',
                          wordBreak: 'break-word', fontSize: '11px', lineHeight: '1.5'
                        }}>
                          {threat.threat_description || 'No description'}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {getSeverityBadge(threat.asset_criticality, threat.wazuh_rule_level)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <a
                          href={`/ThreatGuard?id=${threat.threat_id}`}
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'transparent',
                            color: ORANGE_COLORS.primary,
                            border: `1px solid ${ORANGE_COLORS.primary}`,
                            padding: '5px 12px', borderRadius: '6px',
                            fontSize: '11px', fontWeight: '500',
                            textDecoration: 'none', transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => { e.target.style.backgroundColor = ORANGE_COLORS.primary; e.target.style.color = 'white'; }}
                          onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = ORANGE_COLORS.primary; }}
                        >
                          <i className="ri-eye-line me-1" />View
                        </a>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">
                      <div className="mx-auto mb-3" style={threatIconChip()}>
                        <i className="ri-windows-line" style={{ color: "var(--vz-primary)", fontSize: 16 }} />
                      </div>
                      <h6 className="text-muted">
                        No {severity !== 'all' ? severity + ' ' : ''}Windows threats found
                      </h6>
                      <p className="text-muted small mb-0">No threats detected for Windows systems</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default WindowsThreatsTable;
